import { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import ProfileSwitcher from './ProfileSwitcher';
import NotificationBell from './notifications/NotificationBell';
import { toast } from 'sonner';
import {
  ChartBar, Buildings, Users, Scan, Queue, CopySimple,
  Export, ShieldCheck, Target, SignOut, GearSix, Brain, EnvelopeSimple,
  FileArrowDown, FileText, ChatCircle, BookOpen, GraduationCap, MagnifyingGlass, Tray, Lock, Link as LinkIcon,
  CloudArrowDown, ArrowsLeftRight, CaretDown, CaretRight, Star, PushPin
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_aquatic-crm-sync/artifacts/d0yhpcoq_PoolFIX%20-%20Logo%20-%20RGB%20350px.jpg";

const _sortByLabel = (arr) =>
  arr.slice().sort((a, b) => a.label.localeCompare(b.label, 'nl', { sensitivity: 'base' }));

// Hoofdmenu — alle kern-functionaliteiten, alfabetisch gesorteerd.
const MAIN_ITEMS = _sortByLabel([
  { path: '/dashboard', label: 'Dashboard', icon: ChartBar },
  { path: '/facilities', label: 'Facilities', icon: Buildings },
  { path: '/contacts', label: 'Contacts', icon: Users },
  { path: '/lead-scores', label: 'Lead Scores', icon: Target },
  { path: '/enrichment', label: 'AI Enrichment', icon: Brain },
  { path: '/email', label: 'Marketing', icon: EnvelopeSimple },
  { path: '/global-inbox', label: 'Centrale Inbox', icon: Tray },
  { path: '/inbox', label: 'Inbox', icon: Tray },
  { path: '/learning', label: 'Leerinzichten', icon: GraduationCap },
  { path: '/search-terms', label: 'Zoektermen', icon: MagnifyingGlass },
  { path: '/conversations', label: 'Conversaties', icon: ChatCircle },
  { path: '/knowledge-base', label: 'Kennisbank', icon: BookOpen },
  { path: '/import-export', label: 'Import / Export', icon: FileArrowDown },
  { path: '/scans', label: 'Scan Jobs', icon: Scan },
  { path: '/duplicates', label: 'Duplicates', icon: CopySimple },
  { path: '/exports', label: 'Exact Exports', icon: Export },
  { path: '/compliance', label: 'Compliance', icon: ShieldCheck },
]);

// Configuratie-items — verzameld onder een collapsible Instellingen-groep.
const SETTINGS_ITEMS = _sortByLabel([
  { path: '/settings/mailboxes', label: 'Verzendadressen', icon: EnvelopeSimple },
  { path: '/settings/ai-guidelines', label: 'AI-richtlijnen', icon: Brain },
  { path: '/settings/reply-learning', label: 'Inbox AI Leer-overzicht', icon: GraduationCap },
  { path: '/settings/article-mappings', label: 'Klant-artikel mappings', icon: LinkIcon },
  { path: '/settings/security', label: 'Beveiliging', icon: Lock },
  { path: '/settings/backups', label: 'Backups', icon: CloudArrowDown },
]);

const ALL_ITEMS_BY_PATH = Object.fromEntries(
  [...MAIN_ITEMS, ...SETTINGS_ITEMS].map((i) => [i.path, i]),
);
const FAVORITES_STORAGE_KEY = 'poolfix.sidebar.favorites.v1';
const MAX_FAVORITES = 5;

function _loadFavorites() {
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Filter out any paths that no longer exist in current nav config
    return parsed.filter((p) => typeof p === 'string' && ALL_ITEMS_BY_PATH[p]);
  } catch {
    return [];
  }
}

/**
 * Single sidebar row with pin-toggle.
 * - Always renders the NavLink so the active highlight + react-router routing
 *   work identically to before.
 * - The pin button (star) sits on the right and is keyboard-reachable. We stop
 *   propagation so clicking the star never navigates.
 */
function NavItem({ item, isPinned, onPinToggle, onNavigate, indent = false, badge, breakdown }) {
  const Icon = item.icon;
  const [showBreakdown, setShowBreakdown] = useState(false);
  return (
    <div className="group/nav relative">
      <NavLink
        to={item.path}
        onClick={onNavigate}
        data-testid={`nav-${item.path.slice(1).replace(/\//g, '-')}`}
        className={({ isActive }) =>
          `flex items-center gap-3 ${indent ? 'pl-12 pr-2 py-2 text-[13px]' : 'px-5 py-3 text-sm'} transition-colors duration-150 ${
            isActive
              ? 'bg-zinc-100 text-[#0F172A] font-medium border-l-2 border-l-[#0055FF]'
              : `${indent ? 'text-zinc-500' : 'text-zinc-600'} hover:bg-zinc-50 hover:text-zinc-900 border-l-2 border-l-transparent`
          }`
        }
      >
        <Icon size={indent ? 15 : 18} weight="regular" />
        <span className="flex-1 truncate">{item.label}</span>
        {badge > 0 && (
          <span
            data-testid={`nav-badge-${item.path.slice(1).replace(/\//g, '-')}`}
            className="font-data text-[10px] font-bold bg-[#0055FF] text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        {breakdown && breakdown.length > 1 && (
          <button
            type="button"
            data-testid={`nav-breakdown-toggle-${item.path.slice(1).replace(/\//g, '-')}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowBreakdown((v) => !v); }}
            title={showBreakdown ? 'Verberg per-postbus telling' : 'Toon per-postbus telling'}
            className="text-zinc-400 hover:text-zinc-700 p-0.5"
          >
            {showBreakdown ? <CaretDown size={11} /> : <CaretRight size={11} />}
          </button>
        )}
      </NavLink>
      {showBreakdown && breakdown && breakdown.length > 0 && (
        <ul
          data-testid={`nav-breakdown-${item.path.slice(1).replace(/\//g, '-')}`}
          className="pl-12 pr-3 pb-2 space-y-0.5"
        >
          {breakdown.map((b) => (
            <li key={b.mailbox} className="flex items-center justify-between text-[11px] text-zinc-500" title={b.mailbox}>
              <span className="truncate">{b.mailbox.split('@')[0]}</span>
              <span className="font-data font-bold text-zinc-700 ml-2">{b.count}</span>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={(e) => onPinToggle(item.path, e)}
        title={isPinned ? 'Verwijder uit favorieten' : 'Voeg toe aan favorieten'}
        aria-label={isPinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
        data-testid={`pin-toggle-${item.path.slice(1).replace(/\//g, '-')}`}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-sm transition-opacity ${
          isPinned
            ? 'opacity-100 text-amber-500 hover:bg-amber-50'
            : 'opacity-0 group-hover/nav:opacity-100 focus:opacity-100 text-zinc-300 hover:text-amber-500 hover:bg-zinc-100'
        }`}
      >
        <Star size={13} weight={isPinned ? 'fill' : 'regular'} />
      </button>
    </div>
  );
}

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const { activeProfile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  // Auto-expand the Instellingen-groep when the user navigates straight to a
  // settings route (e.g. via deep-link or after page-refresh).
  const [settingsOpen, setSettingsOpen] = useState(
    () => location.pathname.startsWith('/settings/'),
  );
  // Pinned favorites — persisted in localStorage per browser.
  const [favorites, setFavorites] = useState(() => _loadFavorites());

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      /* quota / private mode — silent */
    }
  }, [favorites]);

  const toggleFavorite = useCallback((path, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setFavorites((prev) => {
      if (prev.includes(path)) {
        return prev.filter((p) => p !== path);
      }
      if (prev.length >= MAX_FAVORITES) {
        toast.error(`Maximaal ${MAX_FAVORITES} favorieten — verwijder eerst een ster`);
        return prev;
      }
      return [...prev, path];
    });
  }, []);

  // Resolve the favorite paths to full item-objects, preserving the user's
  // pin-order so the most-recently pinned is at the bottom of the group.
  const favoriteItems = useMemo(
    () => favorites.map((p) => ALL_ITEMS_BY_PATH[p]).filter(Boolean),
    [favorites],
  );
  const pinnedSet = useMemo(() => new Set(favorites), [favorites]);
  const visibleMain = useMemo(
    () => MAIN_ITEMS.filter((i) => !pinnedSet.has(i.path)),
    [pinnedSet],
  );
  const visibleSettings = useMemo(
    () => SETTINGS_ITEMS.filter((i) => !pinnedSet.has(i.path)),
    [pinnedSet],
  );

  const [inboxUnread, setInboxUnread] = useState({ total: 0, byMailbox: [] });
  const [globalInboxUnread, setGlobalInboxUnread] = useState(0);

  // Poll inbox stats every 60s so per-mailbox unread counters stay fresh
  // from any page. Resets when the active profile switches.
  useEffect(() => {
    let alive = true;
    const fetchUnread = async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`${API}/api/email/inbox/stats`, { credentials: 'include' }),
          fetch(`${API}/api/email/global-inbox/stats`, { credentials: 'include' }),
        ]);
        if (r1.ok) {
          const d = await r1.json();
          if (!alive) return;
          setInboxUnread({
            total: d.nieuw || 0,
            byMailbox: d.unread_by_mailbox || [],
          });
        }
        if (r2.ok) {
          const d = await r2.json();
          if (!alive) return;
          setGlobalInboxUnread(d.grand_unread || 0);
        }
      } catch { /* network errors are fine — try again next tick */ }
    };
    fetchUnread();
    // Poll inbox stats every 3 min (was 60s). Cheaper on Mongo + bandwidth;
    // user can always pull-to-refresh by switching profile or visiting /inbox.
    const id = setInterval(fetchUnread, 180_000);
    return () => { alive = false; clearInterval(id); };
  }, [activeProfile]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Compose absolute URL for relative logo paths from backend
  const logoSrc = activeProfile?.logo_url
    ? (activeProfile.logo_url.startsWith('http') ? activeProfile.logo_url : `${API}${activeProfile.logo_url}`)
    : FALLBACK_LOGO;
  const accent = activeProfile?.color || '#0F172A';

  // Compute per-NavItem extras (currently only Inbox gets a badge + breakdown).
  const itemExtras = useCallback((item) => {
    if (item.path === '/inbox') {
      return { badge: inboxUnread.total, breakdown: inboxUnread.byMailbox };
    }
    if (item.path === '/global-inbox') {
      return { badge: globalInboxUnread };
    }
    return {};
  }, [inboxUnread, globalInboxUnread]);

  return (
    <aside className="w-64 min-h-screen border-r border-zinc-200 bg-white flex flex-col" data-testid="sidebar" style={{ borderTopColor: accent, borderTopWidth: '3px', borderTopStyle: 'solid' }}>
      {/* Logo + notification bell */}
      <div className="flex items-center justify-between gap-2 px-5 py-5 border-b border-zinc-200">
        <img src={logoSrc} alt={activeProfile?.name || 'Logo'} className="h-7 w-auto max-w-[120px] object-contain" />
        <NotificationBell activeProfile={activeProfile} />
      </div>

      {/* Profile switcher (multi-tenant) */}
      <ProfileSwitcher />

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {/* ─── Favorieten ─── */}
        {favoriteItems.length > 0 && (
          <div className="mb-2 border-b border-zinc-100 pb-2" data-testid="favorites-group">
            <div className="px-5 py-1.5 flex items-center gap-1.5">
              <Star size={11} weight="fill" className="text-amber-500" />
              <span className="text-[10px] uppercase font-semibold tracking-wider text-zinc-500">Favorieten</span>
            </div>
            {favoriteItems.map((item) => (
              <NavItem
                key={item.path}
                item={item}
                isPinned
                onPinToggle={toggleFavorite}
                onNavigate={onNavigate}
                {...itemExtras(item)}
              />
            ))}
          </div>
        )}

        {/* ─── Hoofdmenu ─── */}
        {visibleMain.map((item) => (
          <NavItem
            key={item.path}
            item={item}
            isPinned={false}
            onPinToggle={toggleFavorite}
            onNavigate={onNavigate}
            {...itemExtras(item)}
          />
        ))}

        {/* Instellingen — collapsible group with all settings/* routes */}
        {visibleSettings.length > 0 && (
        <div className="mt-2 border-t border-zinc-100" data-testid="settings-group">
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            aria-expanded={settingsOpen}
            data-testid="settings-group-toggle"
            className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-colors duration-150 border-l-2 ${
              location.pathname.startsWith('/settings/')
                ? 'bg-zinc-100 text-[#0F172A] font-medium border-l-[#0055FF]'
                : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 border-l-transparent'
            }`}
          >
            <GearSix size={18} weight="regular" />
            <span className="flex-1 text-left">Instellingen</span>
            {settingsOpen
              ? <CaretDown size={14} className="text-zinc-400" />
              : <CaretRight size={14} className="text-zinc-400" />}
          </button>
          {settingsOpen && (
            <div className="pb-1" data-testid="settings-group-items">
              {visibleSettings.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  isPinned={false}
                  onPinToggle={toggleFavorite}
                  onNavigate={onNavigate}
                  indent
                />
              ))}
            </div>
          )}
        </div>
        )}
      </nav>

      {/* User / Logout */}
      <div className="border-t border-zinc-200 p-4">
        {user && (
          <div className="flex items-center gap-3 mb-3">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-600">
                {user.name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user.name || 'User'}</div>
              <div className="text-xs text-zinc-400 truncate">{user.email}</div>
            </div>
          </div>
        )}
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-zinc-500 hover:text-zinc-800 hover:bg-zinc-50 rounded-sm transition-colors"
        >
          <SignOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );
}
